import { z } from 'zod/v4';
import {
  CreateOrderLineInputSchema,
  UpsertOrderLineInputSchema,
} from './order-line.js';

/**
 * The order's trade direction. System-computed from the buyer / supplier /
 * owning company — never client-settable (excluded from create and update).
 */
export const ORDER_TYPE_VALUES = [
  'PURCHASE_ORDER',
  'SALES_ORDER',
  'INTERNAL_ORDER',
] as const;

export type OrderType = (typeof ORDER_TYPE_VALUES)[number];

/** Client-settable order sub-type. */
export const ORDER_SUB_TYPE_VALUES = [
  'STANDARD',
  'REPEAT',
  'COMMIT',
  'SAMPLE',
  'EXTRACTION_SPLIT',
] as const;

export type OrderSubType = (typeof ORDER_SUB_TYPE_VALUES)[number];

/** Client-settable order workflow status. */
export const ORDER_STATUS_VALUES = [
  'IN_PROGRESS',
  'IN_REVIEW',
  'COMPLETED',
] as const;

export type OrderStatus = (typeof ORDER_STATUS_VALUES)[number];

export const OrderResourceSchema = z
  .object({
    id: z.string(),
    forCompanyId: z.string(),
    orderNumber: z.string(),
    orderType: z.enum(ORDER_TYPE_VALUES).nullable(),
    // `orderSubType` / `status` are validated as enums on write (create/update)
    // but kept lenient on read so legacy/pre-enum rows never fail serialization.
    orderSubType: z.string().nullable(),
    status: z.string().nullable(),
    tags: z.array(z.string()),
    source: z.string().nullable(),
    buyerCompanyId: z.string().nullable(),
    supplierCompanyId: z.string().nullable(),
    receiverCompanyId: z.string().nullable(),
    vendorCompanyId: z.string().nullable(),
    factory: z.string().nullable(),
    buyerFacilityProcess: z.string().nullable(),
    supplierFacilityProcess: z.string().nullable(),
    receiverFacilityProcess: z.string().nullable(),
    orderDate: z.string().nullable(),
    deliveryDate: z.string().nullable(),
    agreedDeliveryDate: z.string().nullable(),
    expectedShippingDate: z.string().nullable(),
    shippingDate: z.string().nullable(),
    expectedDeliveryDate: z.string().nullable(),
    actualDeliveryDate: z.string().nullable(),
    shipmentReference: z.string().nullable(),
    internalResponsiblePerson: z.string().nullable(),
    season: z.string().nullable(),
    counterpartyOrderNumberRef: z.string().nullable(),
    isArchived: z.boolean(),
    tracingStatus: z.string().nullable(),
    createdAt: z.string().nullable(),
    createdByUserId: z.string().nullable(),
    createdByUserName: z.string().nullable(),
    createdByCompanyId: z.string().nullable(),
    createdByCompanyName: z.string().nullable(),
    createdByImpersonateId: z.string().nullable(),
    createdByImpersonateName: z.string().nullable(),
    createdByApiKeyId: z.string().nullable(),
    createdByApiKeyLabel: z.string().nullable(),
    updatedAt: z.string().nullable(),
    updatedByUserId: z.string().nullable(),
    updatedByUserName: z.string().nullable(),
    updatedByCompanyId: z.string().nullable(),
    updatedByCompanyName: z.string().nullable(),
    updatedByImpersonateId: z.string().nullable(),
    updatedByImpersonateName: z.string().nullable(),
    updatedByApiKeyId: z.string().nullable(),
    updatedByApiKeyLabel: z.string().nullable(),
  })
  .strict();

export type OrderResource = z.infer<typeof OrderResourceSchema>;

const str = z.string().max(255);
const idStr = z.string().max(36);

// Strict ISO calendar date (YYYY-MM-DD). We keep the full `z.iso.date()`
// validation but override its JSON-Schema output: the auto-generated RFC 3339
// `pattern` is enormous and unreadable in the API docs, so we drop it and
// surface the friendlier `format: date` + an example instead.
const OrderDateSchema = z.iso.date().meta({
  pattern: undefined,
  format: 'date',
  examples: ['2026-07-21'],
});

export const CreateOrderInputSchema = z
  .object({
    orderNumber: str.min(1),
    orderSubType: z.enum(ORDER_SUB_TYPE_VALUES).optional(),
    status: z.enum(ORDER_STATUS_VALUES).optional(),
    tags: z.array(z.string()).optional(),
    source: str.optional(),
    buyerCompanyId: idStr.min(1),
    supplierCompanyId: idStr.min(1),
    receiverCompanyId: idStr.optional(),
    vendorCompanyId: idStr.optional(),
    factory: idStr.optional(),
    buyerFacilityProcess: str.optional(),
    supplierFacilityProcess: str.optional(),
    receiverFacilityProcess: str.optional(),
    orderDate: OrderDateSchema.optional(),
    deliveryDate: OrderDateSchema.optional(),
    agreedDeliveryDate: OrderDateSchema.optional(),
    expectedShippingDate: OrderDateSchema.optional(),
    shippingDate: OrderDateSchema.optional(),
    expectedDeliveryDate: OrderDateSchema.optional(),
    actualDeliveryDate: OrderDateSchema.optional(),
    shipmentReference: str.optional(),
    // Accepts an email OR a userId; the service resolves it to a userId before
    // persistence, so allow up to email length (255) rather than the id length.
    internalResponsiblePerson: str.optional(),
    season: z.string().optional(),
    counterpartyOrderNumberRef: z.string().optional(),
    orderType: z.enum(ORDER_TYPE_VALUES).nullable().optional(),
    tracingStatus: str.nullable().optional(),
    isArchived: z.boolean().optional(),
  })
  .strict();

export type CreateOrderInput = z.infer<typeof CreateOrderInputSchema>;

export const CreateOrderRequestSchema = CreateOrderInputSchema.extend({
  lines: z.array(CreateOrderLineInputSchema).optional(),
}).strict();

export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>;

export const UpdateOrderInputSchema = CreateOrderInputSchema.partial().extend({
  orderSubType: z.enum(ORDER_SUB_TYPE_VALUES).nullable().optional(),
  status: z.enum(ORDER_STATUS_VALUES).nullable().optional(),
  source: str.nullable().optional(),
  buyerCompanyId: idStr.min(1).optional(),
  supplierCompanyId: idStr.min(1).optional(),
  receiverCompanyId: idStr.nullable().optional(),
  vendorCompanyId: idStr.nullable().optional(),
  factory: idStr.nullable().optional(),
  buyerFacilityProcess: str.nullable().optional(),
  supplierFacilityProcess: str.nullable().optional(),
  receiverFacilityProcess: str.nullable().optional(),
  orderDate: OrderDateSchema.nullable().optional(),
  deliveryDate: OrderDateSchema.nullable().optional(),
  agreedDeliveryDate: OrderDateSchema.nullable().optional(),
  expectedShippingDate: OrderDateSchema.nullable().optional(),
  shippingDate: OrderDateSchema.nullable().optional(),
  expectedDeliveryDate: OrderDateSchema.nullable().optional(),
  actualDeliveryDate: OrderDateSchema.nullable().optional(),
  shipmentReference: str.nullable().optional(),
  internalResponsiblePerson: str.nullable().optional(),
  season: z.string().nullable().optional(),
  counterpartyOrderNumberRef: z.string().nullable().optional(),
  isArchived: z.boolean().optional(),
  // Declarative full-sync of the order's lines. Omit to leave lines untouched.
  // When present, the array is the desired final set: entries with an `id`
  // update that line, entries without one are created, and any existing line
  // absent from the array is deleted.
  lines: z.array(UpsertOrderLineInputSchema).optional(),
});

export type UpdateOrderInput = z.infer<typeof UpdateOrderInputSchema>;

/** Whitelisted columns the LIST endpoint may sort on (`field:DIR`). */
export const ORDER_SORT_FIELDS = [
  'updatedAt',
  'createdAt',
  'orderNumber',
  'orderDate',
  'status',
] as const;
